/* eslint-disable import/no-unresolved */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider } from 'react-hook-form';

import IconMC from '../../../components/IconMC';
import InputMC from '../../../components/InputMC';
import CurrencyInputMC from '../../../components/CurrencyInputMC';
import PaymentMethod from './PaymentMethod';
import PaymentMethodsList from './PaymentMethodsList';
import { formatMoney } from '../../../../util/numberUtils';

const MAX_DESCRIPTION_LENGTH = 200;

const PaymentLinksOrderData = ({
  formMethods,
  paymentMethods,
  selectedPaymentMethods,
  handleSelected,
  currency,
  currency_symbol,
  formattedDateTime
}) => {
  const { t } = useTranslation('Inbox');
  const descriptionValue = formMethods.watch('description') || '';
  const amountValue = formMethods.watch('amount') || '';

  return (
    <FormProvider {...formMethods}>
      <div className="tw-flex tw-flex-col tw-gap-4">
        <div className="tw-flex tw-flex-col tw-gap-2">
          <span className="tw-font-semibold tw-text-gray-950 tw-text-sm">{t('sendPaymentLinks.orderInformation')}</span>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <div className="tw-relative">
              <CurrencyInputMC
                id="amount"
                name="amount"
                label={t('sendPaymentLinks.amount')}
                useFloatingLabel
              />
              <span className="tw-absolute tw-right-4 tw-transform -tw-translate-y-6 tw-text-gray-400 tw-text-xs">
                {currency}
              </span>
            </div>

            <div className="tw-relative">
              <InputMC
                id="description"
                name="description"
                label={t('sendPaymentLinks.description_label')}
                rows={3}
                textarea
                useFloatingLabel
                maxLength={MAX_DESCRIPTION_LENGTH}
                className="tw-resize-none"
              />
              <span className="tw-absolute tw-right-4 tw-transform -tw-translate-y-6 tw-text-gray-400 tw-text-xs">
                {descriptionValue.length}
                /
                {MAX_DESCRIPTION_LENGTH}
              </span>
            </div>
          </div>
        </div>

        <p className="tw-text-gray-950 tw-text-md tw-font-semibold tw-mb-0">
          {t('sendPaymentLinks.subtitle')}
        </p>

        <div className="tw-flex tw-flex-col tw-gap-3">
          <span className="tw-font-medium tw-text-gray-950">
            {t('sendPaymentLinks.selectPlatforms')}
          </span>
          <div className="tw-flex tw-flex-col tw-gap-1.5">
            {paymentMethods.map((method) => (
              <PaymentMethod key={`method-${method.key}`} method={method} handleSelected={handleSelected} />
            ))}
          </div>
        </div>

        {(descriptionValue || amountValue || selectedPaymentMethods.length > 0) && (
          <div className="tw-flex tw-flex-col tw-gap-4">
            <span className="tw-font-medium tw-text-gray-950">{t('sendPaymentLinks.preview')}</span>
            <div className="tw-flex tw-flex-col tw-self-center tw-p-3 tw-rounded-xl tw-bg-[#D9FDD3] tw-gap-2 tw-text-xs tw-w-full tw-min-w-sm tw-max-w-sm">
              <span className="tw-font-bold tw-gray-950">{t('sendPaymentLinks.orderDetailTitle')}</span>
              {descriptionValue && (
                <div className="tw-text-gray-950 tw-break-words tw-overflow-wrap-anywhere tw-w-full">{descriptionValue}</div>
              )}
              {amountValue && (() => {
                const cleanAmount = String(amountValue).replace(/,/g, '').replace(/[^\d.-]/g, '');
                const amountNumber = cleanAmount ? Number(cleanAmount) : 0;
                return (
                  <div className="tw-text-gray-950">
                    {t('sendPaymentLinks.total')}
                    :
                    {' '}
                    {currency_symbol}
                    {formatMoney(amountNumber)}
                    {' '}
                    {currency}
                  </div>
                );
              })()}
              {selectedPaymentMethods.length > 0 && (
                <>
                  <div className="tw-font-bold tw-gray-950 tw-mt-2">{t('sendPaymentLinks.availablePaymentMethods')}</div>
                  <PaymentMethodsList
                    selectedPaymentMethods={selectedPaymentMethods}
                    keyPrefix="preview"
                    ulClassName="tw-pl-5 tw-mb-1"
                    liClassName="tw-text-gray-950"
                    linkClassName="tw-text-blue-500"
                  />
                </>
              )}
              <div className="tw-flex tw-justify-end tw-items-center tw-gap-2 tw-mt-2">
                <span className="tw-text-xs tw-text-gray-500">{formattedDateTime}</span>
                <IconMC name="double-check" className="tw-text-blue-500" size="base" />
              </div>
            </div>
          </div>
        )}
      </div>
    </FormProvider>
  );
};

export default PaymentLinksOrderData;

